/**
 * `StepHeading` — onboarding step heading, optionally with a numbered
 * badge.
 *
 * Replaces the mono "STEP N · UPPERCASE" kickers inside the install panels
 * with a Geist 16px/600 sentence-case heading. When `n` is a positive
 * integer, the heading is preceded by a 20×20 square badge containing
 * the step number (mono 11/600). When `n` is omitted the heading
 * renders without a badge — the single-step case for a tab that has
 * only one step in view (founder 2026-10-21: a lone "Step 1" numeral
 * reads as a missing Step 2). When `done` is set, the badge inverts
 * to `limeDim` + `limeFg` to signal completion; `done` is ignored
 * when `n` is omitted.
 *
 * Mono step kickers remain correct for category labels (THIS INSTALL COVERS,
 * REDIRECT · RECOMMENDED, WORKER ACTIVE / DOMAIN VERIFIED status, host
 * chips) and for the outer wizard position ("STEP 3 · INSTALL AND VERIFY").
 * This atom is for the INSIDE-the-panel steps only.
 */

import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface StepHeadingProps {
  /** Omit entirely when the tab has only one step in view — the
   *  numbered badge is a hierarchy signal, not decoration. */
  readonly n?: number;
  readonly children: ReactNode;
  readonly done?: boolean;
  readonly as?: 'div' | 'h3' | 'h4';
}

export function StepHeading({
  n,
  children,
  done = false,
  as = 'h4',
}: StepHeadingProps): JSX.Element {
  const C = useC();
  const Tag = as;
  const showBadge = typeof n === 'number';
  return (
    <div
      className="flex items-center"
      style={{ gap: 8, marginBottom: 4 }}
    >
      {showBadge ? (
        <span
          aria-hidden
          data-testid="step-heading-badge"
          className="flex items-center justify-center"
          style={{
            width: 20,
            height: 20,
            borderRadius: 2,
            background: done ? C.limeDim : C.lime,
            color: done ? C.limeFg : C.ink,
            fontFamily: monoFont,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {n}
        </span>
      ) : null}
      <Tag
        style={{
          fontFamily: sansFont,
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: 0,
          color: C.text,
          margin: 0,
        }}
      >
        {children}
      </Tag>
    </div>
  );
}
